/* eslint-disable react-hooks/exhaustive-deps */
import { motion } from "motion/react";

import { RefObject, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { IconPictureInPicture } from "@/components/icons/player.icons";

import { Broadcast } from "@/api/broadcast/broadcast.types";

import Controller from "../Controller";
import FullscreenButton from "../buttons/FullscreenButton";
import MuteButton from "../buttons/MuteButton";
import PlayButton from "../buttons/PlayButton";
import { usePlayerControls2 } from "../hooks/PlayerControlsContext";
import { usePlayerControls } from "../hooks/usePlayerControls";
import OpenBroadCastSidebar from "./OpenBroadCastSidebar";

const LivePlayerUI = ({ data, videoRef }: { data?: Broadcast; videoRef: RefObject<HTMLVideoElement> }) => {
  const { toggleFullScreen, togglePlay, isPlaying, toggleMute, isMuted, volume, setVolumeLevel } =
    usePlayerControls(videoRef);
  const { showControls, setShowControls, controlsLock, setControlsLock } = usePlayerControls2();

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const videoContainer = document.getElementById("player-container");

    const enableMouseMove = () => {
      if (!controlsLock) return;
      setShowControls(true);

      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        if (isPlaying) {
          setShowControls(false);
        }
      }, 3000);
    };

    if (videoContainer) {
      videoContainer.addEventListener("mousemove", enableMouseMove);
    }

    return () => {
      if (videoContainer) {
        videoContainer.removeEventListener("mousemove", enableMouseMove);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [controlsLock, isPlaying, showControls]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (showControls && isPlaying && controlsLock) {
      timeout = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }

    return () => clearTimeout(timeout);
  }, [showControls, isPlaying, controlsLock]);

  const handlePictureInPicture = async () => {
    try {
      if (videoRef.current) {
        if (document.pictureInPictureElement) {
          await document.exitPictureInPicture();
        } else {
          await videoRef.current.requestPictureInPicture();
        }
      }
    } catch (error) {
      console.error("Error enabling Picture-in-Picture:", error);
    }
  };

  const location = useLocation();

  useEffect(() => {
    if (document.pictureInPictureElement) {
      handlePictureInPicture();
    }
  }, [location.pathname]);

  return (
    <div>
      {videoRef && <Controller videoRef={videoRef} />}
      <motion.div
        className="absolute left-0 top-0 flex h-full w-full flex-col"
        initial={false}
        animate={{
          opacity: showControls ? 1 : 0,
          pointerEvents: showControls ? "auto" : "none",
        }}
        transition={{ duration: 0.2 }}
      >
        <div className="flex  justify-between px-6 pt-6">
          <div className="flex items-center gap-3">
            {data?.icon && <img src={data?.icon} alt={data?.title} className="size-16 rounded-xl" />}
            <p className="text-[22px] font-medium">{data?.title}</p>
          </div>
          <div className="flex items-center gap-4">
            <div onClick={handlePictureInPicture}>
              <IconPictureInPicture className="player-icon !fill-transparent stroke-white" />
            </div>
            <OpenBroadCastSidebar />
          </div>
        </div>
        <div className="h-full w-full" onClick={togglePlay}></div>
        <div
          className="flex items-center justify-between px-6 py-6"
          onMouseEnter={() => setControlsLock(false)}
          onMouseLeave={() => setControlsLock(true)}
        >
          <div className="flex items-center gap-4">
            <PlayButton togglePlay={togglePlay} isPlaying={isPlaying} />
            <MuteButton toggleMute={toggleMute} isMuted={isMuted} volume={volume} setVolumeLevel={setVolumeLevel} />
          </div>
          <FullscreenButton toggleFullScreen={toggleFullScreen} />
        </div>
      </motion.div>
    </div>
  );
};

export default LivePlayerUI;
